import { useState, useRef, useEffect, useMemo } from 'react';
import type { KeyboardEvent } from 'react';
import styled from '@emotion/styled';
import { theme, scaled } from '../styles/theme';
import type { FilterOption } from '../types';
import { useT } from '../i18n';

// Mirrors the dropdown-labeled / .dropdown structure used in CascadingSelect
// — see that file's leading comments for the rationale.
const Container = styled.div<{ isOpen: boolean }>`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  gap: var(--space-1);
  width: 100%;
  /* As a grid item in FilterPanel's FilterGrid, the default min-width:auto
     would size the track to the nowrap DisplayText (a long selected label),
     letting the grid overflow the filter card. min-width:0 keeps the pill at
     its track width; DisplayText then ellipsizes. Same fix as CascadingSelect. */
  min-width: 0;
  /* Local to FilterCard's stacking context — open select overlaps siblings. */
  z-index: ${props => props.isOpen ? 3 : 2};
`;

const Label = styled.label<{ disabled?: boolean }>`
  font-family: var(--sans-stack);
  font-size: ${scaled(13)};
  line-height: 1.2;
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-text-default)'};
  /* Scaled off the widget base, not em, so the label's left edge lines up with
     the trigger text: the button pads 0.8em at 16px (= 12.8px), so a plain
     0.8em here at 13px would under-indent and the label would sit too far
     left. */
  padding: 0 ${scaled(12.8)};
  opacity: ${props => props.disabled ? 0.6 : 1};
`;

const SelectButton = styled.button<{ isOpen: boolean; hasValue: boolean; disabled?: boolean }>`
  font-family: var(--sans-stack);
  font-size: ${scaled(16)};
  line-height: 1.2;
  padding: 0.3em 2.6em 0.2em 0.8em;
  border-radius: 999px;
  border: 1px solid ${props => props.isOpen ? 'var(--color-text-default)' : 'rgba(0, 0, 0, 0.1)'};
  background-color: ${props => props.disabled ? '#f5f5f5' : 'transparent'};
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 -960 960 960' width='24' fill='%235f6368'%3E%3Cpath d='M480-360 280-560h400L480-360Z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.1em center;
  background-size: 2em;
  color: ${props => props.disabled ? 'var(--color-text-muted)' : props.hasValue ? 'var(--color-text-default)' : 'var(--color-text-muted)'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  outline: none;
  width: 100%;
  text-align: left;
  position: relative;
  transition: border-color 0.2s, background-color 0.2s;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:focus-visible {
    outline: 0;
    box-shadow: ${props => props.disabled ? 'none' : '0 0 0 3px var(--color-focus-ring)'};
  }

  &:hover {
    background-color: ${props => props.disabled ? '#f5f5f5' : 'rgba(0, 0, 0, 0.03)'};
  }
`;

const ChevronIcon = styled.span<{ isOpen: boolean }>`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%) ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.2s ease;
  color: ${theme.colors.text.secondary};
  display: flex;
  align-items: center;
`;

// Panel matches website screenshot: clean white card, subtle shadow, 8px
// radius, no border. Open/close transition kept (visibility cross-fade).
const Dropdown = styled.div<{ isOpen: boolean; minWidth?: string }>`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  min-width: ${props => props.minWidth || '100%'};
  width: 100%;
  background: #ffffff;
  border-radius: var(--radius-m);
  box-shadow: var(--shadow-m);
  /* Local to the select Container's stacking context. */
  z-index: 9;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.98)'};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
`;

const SearchInputWrapper = styled.div`
  padding: var(--space-3) var(--space-4);
  position: relative;
  box-sizing: border-box;
`;

// Magnifier on the RIGHT (inside the pill). `right` is anchored to the
// wrapper's right padding edge, and the offset is pixel-based so the icon
// can't drift outside the input pill when the host page changes the root
// font-size (the earlier `0.8em` would scale with em and could overflow).
const SearchInputIcon = styled.span`
  position: absolute;
  right: calc(var(--space-4) + 12px);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const SearchInput = styled.input`
  font-family: var(--sans-stack);
  font-size: ${scaled(13)};
  padding: 0.5em 2.4em 0.5em 0.8em;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 999px;
  background: transparent;
  color: var(--color-text-default);
  outline: none;
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  &::placeholder { color: var(--color-text-muted); }
  &:focus {
    border-color: var(--color-text-default);
  }
`;

const OptionsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 320px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: var(--color-bg-muted); border-radius: 3px; }
`;

const OptionItem = styled.li<{ isHighlighted: boolean; isSelected: boolean }>`
  padding: var(--space-3) var(--space-4);
  cursor: pointer;
  font-family: var(--sans-stack);
  font-size: ${scaled(13)};
  line-height: 1.3;
  color: var(--color-text-default);
  border-bottom: 1px solid var(--color-bg-muted);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-3);

  &:last-child { border-bottom: none; }
  &:hover { background: var(--color-bg-surface); }
  ${props => props.isHighlighted && 'background: var(--color-bg-surface);'}
`;

// Square checkbox matching the website pattern: dark border, fills with
// dark when checked, white check inside. Sized close to the 0.8125rem
// suggestion text so it reads as part of the row, not an oversized control.
const Checkbox = styled.span<{ checked: boolean }>`
  width: 14px;
  height: 14px;
  border: 1.5px solid var(--color-text-default);
  border-radius: 2px;
  background: ${props => props.checked ? 'var(--color-text-default)' : 'transparent'};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
`;

const OptionLabel = styled.span`
  flex: 1;
  line-height: 1.3;
  word-break: break-word;
  min-width: 0;
`;

const OptionCount = styled.span`
  color: var(--color-text-muted);
  margin-left: 0.4em;
`;

const NoResults = styled.div`
  padding: var(--space-6);
  text-align: center;
  color: var(--color-text-muted);
  font-family: var(--sans-stack);
  font-size: ${scaled(13)};
`;

const TruncationNotice = styled.li`
  padding: var(--space-3) var(--space-4);
  font-family: var(--sans-stack);
  font-size: ${scaled(12)};
  color: var(--color-text-muted);
  text-align: center;
  font-style: italic;
`;

const ResultsCount = styled.div`
  padding: var(--space-2) var(--space-4);
  font-family: var(--sans-stack);
  font-size: ${scaled(12)};
  color: var(--color-text-muted);
  border-bottom: 1px solid var(--color-bg-muted);
`;

const DisplayText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 24px;
  font-weight: 450;
`;

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ChevronSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6,9 12,15 18,9" />
  </svg>
);

const CheckSvg = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20,6 9,17 4,12" />
  </svg>
);

interface MultiSearchableSelectProps {
  options: FilterOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  label: string;
  dropdownMinWidth?: string;
  /** Display form of an option label (e.g. stateLabel); the value stays the
      key. The search matches the displayed label and the raw one. */
  formatLabel?: (label: string) => string;
}

const identity = (label: string) => label;

export function MultiSearchableSelect({ options, value, onChange, placeholder, label, dropdownMinWidth, formatLabel = identity }: MultiSearchableSelectProps) {
  const m = useT();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const MAX_VISIBLE = 200;

  // The server counts each dimension's options against the *other* active
  // filters, so a value can stay selected while dropping out of its own option
  // list (no rows left in that combination). Keep such a value listed — the
  // chip bar still shows it as active, and the dropdown is where it gets
  // unticked. Count 0 is honest: that's what the combination yields.
  const allOptions = useMemo(() => {
    const known = new Set(options.map(o => o.value));
    const missing = value.filter(v => !known.has(v)).map(v => ({ value: v, label: v, count: 0 }));
    return missing.length > 0 ? [...options, ...missing] : options;
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return allOptions;
    const lower = searchTerm.toLowerCase();
    return allOptions.filter(opt =>
      formatLabel(opt.label).toLowerCase().includes(lower) || opt.label.toLowerCase().includes(lower));
  }, [allOptions, searchTerm, formatLabel]);

  const visibleOptions = useMemo(
    () => filteredOptions.slice(0, MAX_VISIBLE),
    [filteredOptions],
  );

  const displayValue = useMemo(() => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const opt = allOptions.find(o => o.value === value[0]);
      return formatLabel(opt?.label || value[0]!);
    }
    return m.select.selected(value.length);
  }, [value, allOptions, placeholder, formatLabel, m]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => { setHighlightedIndex(-1); }, [filteredOptions]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('li');
      items[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const toggleOption = (optValue: string) => {
    if (selectedSet.has(optValue)) {
      onChange(value.filter(v => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) setIsOpen(true);
        else setHighlightedIndex(prev => prev < visibleOptions.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && visibleOptions[highlightedIndex]) {
          toggleOption(visibleOptions[highlightedIndex].value);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  return (
    <Container ref={containerRef} isOpen={isOpen}>
      <Label>{label}</Label>
      <SelectButton
        type="button"
        isOpen={isOpen}
        hasValue={value.length > 0}
        onClick={() => { setIsOpen(!isOpen); if (isOpen) setSearchTerm(''); }}
        onKeyDown={handleKeyDown}
      >
        <DisplayText>{displayValue}</DisplayText>
        {/* Chevron is rendered via SelectButton's background-image — see styled definition above. */}
      </SelectButton>

      {isOpen && (
        <Dropdown isOpen minWidth={dropdownMinWidth}>
          <SearchInputWrapper>
            <SearchInputIcon><SearchIcon /></SearchInputIcon>
            <SearchInput
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={m.select.search}
            />
          </SearchInputWrapper>
          {searchTerm && (
            <ResultsCount>{m.select.resultsOf(filteredOptions.length, allOptions.length)}</ResultsCount>
          )}
          {visibleOptions.length > 0 ? (
            <OptionsList ref={listRef}>
              {visibleOptions.map((opt, index) => {
                const isSelected = selectedSet.has(opt.value);
                return (
                  <OptionItem
                    key={opt.value}
                    isHighlighted={index === highlightedIndex}
                    isSelected={isSelected}
                    onClick={() => toggleOption(opt.value)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <Checkbox checked={isSelected}>
                      {isSelected && <CheckSvg />}
                    </Checkbox>
                    <OptionLabel>
                      {formatLabel(opt.label)}
                      <OptionCount>({opt.count})</OptionCount>
                    </OptionLabel>
                  </OptionItem>
                );
              })}
              {filteredOptions.length > MAX_VISIBLE && (
                <TruncationNotice>
                  {m.select.more(filteredOptions.length - MAX_VISIBLE)}
                </TruncationNotice>
              )}
            </OptionsList>
          ) : (
            <NoResults>{m.select.noResultsFor(searchTerm)}</NoResults>
          )}
        </Dropdown>
      )}
    </Container>
  );
}
